// Theme-reactive favicon — recolours the tab icon to the ACTIVE theme's ink the
// instant the theme changes. Call updateFavicon() right after [data-appearance] is
// set (and once on load). The token resolves to rgb via a throwaway element's
// computed colour, because favicon decoders don't reliably parse oklch.

// The Waytide mark path + a viewBox fitted tightly to it (mirrors public/favicon.svg).
const VIEWBOX = "18 14 188 114";
const PATH =
  "m 26.55,109.54 c -6.35,1.18 -12.61,-50.07 2.77,-71.59 16,-22.39 53.3,-27.73 80.8,-13.42 29.5,15.34 33.56,37.16 52.85,45.79 12.13,5.43 19.47,3.44 27.91,-2.12 5.78,-3.81 4.38,-11.05 7.23,-11.7 C 205.86,56.48 214.21,115 172,125 140,133 108.93,106.56 94.65,86.46 81.99,68.65 59.74,71.88 50.19,79.8 35.31,92.14 31.57,108.28 26.55,109.54 Z";

/** Resolve a CSS custom property to a decoder-safe rgb() string. The token is
 * oklch, which favicon decoders don't parse and which modern Chromium preserves
 * through both getComputedStyle AND canvas fillStyle serialisation — so RASTERISE
 * one pixel and read it back as sRGB bytes, which is conversion-proof. */
function resolved(varName: string, fallback: string): string {
  try {
    const raw = getComputedStyle(document.documentElement)
      .getPropertyValue(varName)
      .trim();
    if (!raw) return fallback;
    const cvs = document.createElement("canvas");
    cvs.width = cvs.height = 1;
    const ctx = cvs.getContext("2d", { willReadFrequently: true });
    if (!ctx) return fallback;
    ctx.fillStyle = raw; // oklch ok in modern browsers
    ctx.fillRect(0, 0, 1, 1);
    const [r, g, b] = ctx.getImageData(0, 0, 1, 1).data;
    return `rgb(${r}, ${g}, ${b})`;
  } catch {
    return fallback;
  }
}

/** Repaint the tab favicon in the current theme's ink. Idempotent + cheap. */
export function updateFavicon(): void {
  if (typeof document === "undefined" || !document.body) return;
  const ink = resolved("--waytide-comp-receipt-ink", "#3d6ba6");
  const svg =
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="${VIEWBOX}">` +
    `<path fill="${ink}" d="${PATH}"/></svg>`;
  const href = "data:image/svg+xml," + encodeURIComponent(svg);
  let link = document.querySelector<HTMLLinkElement>('link[rel="icon"]');
  if (!link) {
    link = document.createElement("link");
    link.rel = "icon";
    document.head.appendChild(link);
  }
  link.type = "image/svg+xml";
  link.href = href;
}
